import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './LandingPage.css';
import logo from '../../images/logo_white.png';
import mockup from '../../images/dashboard_mockup.png';
import mockup1 from '../../images/dashboard_mockup1.png';

const LandingPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [whyCurrentSlide, setWhyCurrentSlide] = useState(0);
  const [featureCurrentSlide, setFeatureCurrentSlide] = useState(0);
  const location = useLocation();

  const isMobile = () => window.innerWidth <= 768;

  const faqData = useMemo(
    () => [
      {
        question: 'What is TalentScope360?',
        answer:
          'TalentScope360 is an AI-powered competency intelligence platform that helps organizations assess workforce capability, identify role readiness, surface skill gaps, and support stronger talent decisions across development, promotion, and workforce planning.'
      },
      {
        question: 'How is it different from a traditional performance tool?',
        answer:
          'Traditional performance tools mostly focus on ratings and outcomes. TalentScope360 helps organizations understand the capabilities behind performance, making talent assessment more structured, more consistent, and more actionable.'
      },
      {
        question: 'Can it work with our current HR systems?',
        answer:
          'Yes. TalentScope360 is designed to complement your existing HRIS, performance, compensation, and learning systems by acting as the capability intelligence layer that strengthens talent decision-making.'
      },
      {
        question: 'What decisions can it support?',
        answer:
          'It can support development planning, internal mobility, role readiness, promotion discussions, calibration, succession planning, and broader workforce decisions through competency-based insight.'
      },
      {
        question: 'How are competencies assessed?',
        answer:
          'Competencies are assessed against role-based expectations using structured frameworks, expected levels, weighted models, and evaluation inputs from managers, employees, or panels.'
      },
      {
        question: 'How do we implement it?',
        answer:
          'Implementation typically begins with role architecture, competency framework setup, and assessment design, followed by configuration, onboarding, and rollout support.'
      }
    ],
    []
  );

  const featureCards = useMemo(
    () => [
      {
        title: 'Competency Frameworks',
        description:
          'Define the capabilities, behaviors, and standards required across roles and levels.',
        icon: (
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path
              d="M12 8.5l3 1.7v3.6L12 15.5l-3-1.7v-3.6l3-1.7Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        )
      },
      {
        title: 'Role Mapping',
        description:
          'Map competencies to specific roles so expectations are clear and structured.',
        icon: (
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M4 7h7M4 12h5M4 17h7M14 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )
      },
      {
        title: 'Assessment Workflows',
        description:
          'Run structured competency assessments across employees, teams, and functions.',
        icon: (
          <svg viewBox="0 0 24 24" fill="none">
            <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="M8 9h8M8 13h5M8 17h3"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        )
      },
      {
        title: 'Gap Analysis',
        description:
          'Identify strengths, weaknesses, and readiness gaps against expected role standards.',
        icon: (
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M5 18L10 12l3 3 6-8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M18 7h1v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        )
      },
      {
        title: 'Evidence-Based Insights',
        description:
          'Support talent decisions with grounded assessment data, not only subjective opinion.',
        icon: (
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M7 4h10l2 3v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path
              d="M9 10h6M9 14h6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        )
      },
      {
        title: 'HR System Alignment',
        description:
          'Work alongside your existing HR systems without needing to replace them.',
        icon: (
          <svg viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
            <rect x="14" y="5" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
            <rect x="8.5" y="14" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="M10 8h4M12 11v3"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        )
      }
    ],
    []
  );

  const whyCards = useMemo(
    () => [
      {
        title: 'More clarity',
        description:
          'Understand the competencies and behaviors that shape performance, not just the outcomes.'
      },
      {
        title: 'More consistency',
        description:
          'Create a more reliable approach to assessment across managers, teams, and functions.'
      },
      {
        title: 'Better decisions',
        description:
          'Support development, readiness, and growth planning with stronger capability insight.'
      }
    ],
    []
  );

  const impactItems = useMemo(
    () => [
      'Improve consistency in competency assessment across managers and teams',
      'Spot workforce capability gaps earlier and respond more strategically',
      'Support promotion and development decisions with clearer evidence',
      'Connect capability building to long-term organizational growth'
    ],
    []
  );

  const audienceItems = useMemo(
    () => [
      {
        title: 'For HR leaders',
        description:
          'Create a more structured and consistent foundation for talent assessment, development, and workforce planning.'
      },
      {
        title: 'For managers',
        description:
          'Assess team capability more clearly, identify development needs faster, and support stronger calibration conversations.'
      },
      {
        title: 'For executives',
        description:
          'Gain a clearer view of workforce capability and how it connects to growth, readiness, and strategic priorities.'
      }
    ],
    []
  );

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    closeMobileMenu();
  };

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.landingpage-navbar')) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!isMobile()) return undefined;
    const interval = setInterval(() => {
      setWhyCurrentSlide((prev) => (prev + 1) % whyCards.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [whyCards.length]);

  useEffect(() => {
    if (!isMobile()) return undefined;
    const interval = setInterval(() => {
      setFeatureCurrentSlide((prev) => (prev + 1) % featureCards.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [featureCards.length]);

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile()) {
        setWhyCurrentSlide(0);
        setFeatureCurrentSlide(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="landingpage-container">
      <nav className="landingpage-navbar">
        <div className="landingpage-nav-content">
          <div
            className="landingpage-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img src={logo} alt="TalentScope360 Logo" />
          </div>

          <button
            className={`landingpage-mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className="landingpage-nav-links">
            <button onClick={() => scrollToSection('features')} className="landingpage-nav-link">
              Features
            </button>
            <Link to="/pricing" className="landingpage-nav-link">
              Pricing
            </Link>
            <Link to="/documentation" className="landingpage-nav-link">
              Documentation
            </Link>
          </div>

          <div className="landingpage-nav-buttons">
            <a href="/login" className="landingpage-btn-login">Login</a>
            <Link to="/sign-up" className="landingpage-btn-getstarted">Get Started</Link>
          </div>
        </div>

        <div className={`landingpage-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <button onClick={() => scrollToSection('features')} className="landingpage-mobile-menu-link">
            Features
          </button>
          <Link to="/pricing" className="landingpage-mobile-menu-link" onClick={closeMobileMenu}>
            Pricing
          </Link>
          <Link
            to="/documentation"
            className="landingpage-mobile-menu-link"
            onClick={closeMobileMenu}
          >
            Documentation
          </Link>
          <div className="landingpage-mobile-menu-buttons">
            <a href="/login" className="landingpage-btn-login" onClick={closeMobileMenu}>
              Login
            </a>
            <Link to="/sign-up" className="landingpage-btn-getstarted" onClick={closeMobileMenu}>
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      <section className="landingpage-hero">
        <div className="landingpage-hero-bg"></div>
        <div className="landingpage-hero-content">
          <div className="landingpage-hero-text">
            <h1>
              Understand and Grow Workforce Capability — With Measurable Impact on Performance
            </h1>

            <p>
              TalentScope360 is an AI-powered competency intelligence platform that reveals the
              capabilities driving performance, enabling better talent decisions, faster growth,
              and stronger organizational outcomes.
            </p>

            <div className="landingpage-hero-image-mobile">
              <img src={mockup} alt="TalentScope360 platform dashboard" />
            </div>

            <div className="landingpage-hero-buttons">
              <Link to="/demo" className="landingpage-btn-primary">View Demo</Link>
              <Link to="/contact-us" className="landingpage-btn-secondary">Schedule Call</Link>
            </div>
          </div>

          <div className="landingpage-hero-visual">
            <img src={mockup} alt="TalentScope360 platform dashboard" />
          </div>
        </div>
      </section>

      <section className="landingpage-marquee">
        <div className="landingpage-marquee-overlay"></div>
        <div className="landingpage-marquee-content">
          <h2>Align Talent to Strategy</h2>
          <h2>Drive Measurable Performance</h2>
          <h2>Fuel Sustainable Growth</h2>
        </div>
      </section>

      <section className="landingpage-why-section">
        <div className="landingpage-section-header landingpage-why-header">
          <h2>A clearer foundation for talent decisions</h2>
          <p>
            Many organizations have performance data, but still lack a consistent view of the
            capabilities behind it. TalentScope360 helps bring more structure, fairness, and
            clarity to workforce decisions.
          </p>
        </div>

        <div className="landingpage-slider-shell">
          <div
            className="landingpage-why-grid"
            style={isMobile() ? { transform: `translateX(-${whyCurrentSlide * 100}%)` } : undefined}
          >
            {whyCards.map((card, index) => (
              <div className="landingpage-why-card" key={index}>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            ))}
          </div>

          <div className="landingpage-slider-dots landingpage-slider-dots-mobile">
            {whyCards.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`landingpage-slider-dot ${whyCurrentSlide === index ? 'active' : ''}`}
                onClick={() => setWhyCurrentSlide(index)}
                aria-label={`Go to why slide ${index + 1}`}
              />
             ))}
          </div>
        </div>
      </section>

      <section className="landingpage-audience-section">
        <div className="landingpage-audience-wrap">
          <div className="landingpage-audience-intro">
            <p>
              TalentScope360 helps organizations move beyond generic performance management by creating
              a clearer view of workforce capability and using it to support better talent decisions.
            </p>

            <img
              src={mockup1}
              alt="Dashboard preview"
              className="landingpage-audience-image"
            />
          </div>

          <div className="landingpage-audience-grid">
            {audienceItems.map((item, index) => (
              <div className="landingpage-audience-card" key={index}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="landingpage-features">
        <div className="landingpage-section-header landingpage-section-header-light">
          <h2>Performance capabilities built around competency intelligence</h2>
          <p>
            A focused set of capabilities to help organizations define, assess, understand, and act on workforce capability.
          </p>
        </div>

        <div className="landingpage-slider-shell">
          <div
            className="landingpage-features-grid landingpage-features-grid-three"
            style={isMobile() ? { transform: `translateX(-${featureCurrentSlide * 100}%)` } : undefined}
          >
            {featureCards.map((feature, index) => (
              <div className="landingpage-feature-card" key={index}>
                <div className="landingpage-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="landingpage-slider-dots landingpage-slider-dots-mobile landingpage-slider-dots-light">
            {featureCards.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`landingpage-slider-dot ${featureCurrentSlide === index ? 'active' : ''}`}
                onClick={() => setFeatureCurrentSlide(index)}
                aria-label={`Go to feature slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="landingpage-how-impact-wrap">
        <div className="landingpage-how-it-works">
          <div className="landingpage-section-header">
            <h2>How it works</h2>
            <p>
              From defining role expectations to guiding decisions, TalentScope360 helps make competency assessment more usable and actionable.
            </p>
          </div>

          <div className="landingpage-steps-grid">
            <div className="landingpage-step-card">
              <div className="landingpage-step-number">01</div>
              <h3>Define expectations</h3>
              <p>Set competencies and expected levels for roles across the organization.</p>
            </div>
            <div className="landingpage-step-card">
              <div className="landingpage-step-number">02</div>
              <h3>Assess capability</h3>
              <p>Evaluate individuals and teams against structured role-based requirements.</p>
            </div>
            <div className="landingpage-step-card">
              <div className="landingpage-step-number">03</div>
              <h3>Reveal gaps and strengths</h3>
              <p>See what is strong, what is missing, and where development is most needed.</p>
            </div>
            <div className="landingpage-step-card">
              <div className="landingpage-step-number">04</div>
              <h3>Support decisions</h3>
              <p>Use the insights to guide development, readiness, calibration, and planning.</p>
            </div>
          </div>
        </div>

        <div className="landingpage-impact-section">
          <div className="landingpage-impact-panel">
            <h2>Business impact</h2>
            <p>
              A competency-first approach helps organizations make talent decisions with more confidence and direction.
            </p>

            <div className="landingpage-impact-list">
              {impactItems.map((item, index) => (
                <div className="landingpage-impact-item" key={index}>
                  <div className="landingpage-impact-check"></div>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="landingpage-faq-section">
        <div className="landingpage-section-header">
          <h2>Frequently asked questions</h2>
          <p>Everything you need to know about how TalentScope360 fits into your talent strategy.</p>
        </div>

        <div className="landingpage-faq-list">
          {faqData.map((faq, index) => (
            <div key={index} className="landingpage-faq-item">
              <button
                className={`landingpage-faq-question ${openFAQ === index ? 'active' : ''}`}
                onClick={() => toggleFAQ(index)}
              >
                <span>{faq.question}</span>
                <span className="landingpage-faq-icon">{openFAQ === index ? '−' : '+'}</span>
              </button>
              {openFAQ === index && (
                <div className="landingpage-faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="landingpage-cta-section">
        <div className="landingpage-cta-card">
          <div className="landingpage-cta-copy">
            <h2>Start understanding your workforce capability today</h2>
            <p>
              Discover how TalentScope360 can help you improve assessment consistency,
              reveal capability gaps, and support better workforce decisions.
            </p>
          </div>

          <div className="landingpage-cta-actions">
            <Link to="/demo" className="landingpage-btn-card">View Demo</Link>
          </div>
        </div>
      </section>

      <footer className="landingpage-footer">
        <div className="landingpage-footer-pattern"></div>

        <div className="landingpage-footer-content">
          <div className="landingpage-footer-links">
            <div className="landingpage-footer-column">
              <h4>Product</h4>
              <button onClick={() => scrollToSection('features')}>Features</button>
              <button onClick={() => scrollToSection('faq')}>FAQ</button>
              <Link to="/pricing">Pricing</Link>
            </div>

            <div className="landingpage-footer-column">
              <h4>Resources</h4>
              <Link to="/documentation">Documentation</Link>
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>

            <div className="landingpage-footer-column">
              <h4>Company</h4>
              <Link to="/contact-us">Contact Us</Link>
              <a href="/login">Login</a>
              <Link to="/sign-up">Get Started</Link>
            </div>
          </div>

          <div className="landingpage-footer-brand">
            <div className="landingpage-footer-logo">
              <img src={logo} alt="TalentScope360 Logo" />
            </div>
            <p>Competency intelligence for better workforce decisions.</p>
          </div>
        </div>

        <div className="landingpage-footer-bottom">
          <p>
            © {new Date().getFullYear()} TalentScope360 | Crafted with ♡ by{' '}
            <a
              href="https://amun-consulting.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Amun Consulting
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;